import {
  AlertTriangleIcon,
  BugIcon,
  CameraIcon,
  ChevronRightIcon,
  FootprintsIcon,
  MousePointerClickIcon,
  PlayCircleIcon,
  VideoIcon,
} from 'lucide-react';
import { useId, useRef, useState, type JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { artifactUrl } from '@/lib/api/quality.js';
import type {
  ExecutionStep,
  ExecutionStepDetail,
  Finding,
} from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';
import { formatTimecode } from './quality-presentation.js';

import { AuthImage, AuthVideo } from './AuthMedia.js';
import { IMAGE_RE, VIDEO_RE } from './exploration-evidence.js';

// Standalone exploratory sessions surface this panel (QualityRunDetail renders it for
// run_kind === 'exploratory'): a deterministic execution log of what the browse did, the potential
// bugs it flagged, the recorded video of the browse, and a gallery of captured screenshots.
// Display-only. Renders nothing when there's no log, no findings, no recording and no screenshots.

const SEVERITY_VARIANT = {
  high: 'destructive',
  medium: 'warning',
  low: 'secondary',
} as const;

// Artifact keys are stored as full `runs/<id>/...` keys, but artifactUrl wants the path relative to
// the run's prefix (the endpoint re-prepends it). Strip the prefix when present.
function toRelKey(runId: string, key: string): string {
  const prefix = `runs/${runId}/`;
  return key.startsWith(prefix) ? key.slice(prefix.length) : key;
}

// A display-ready execution-log phase: a title, its expandable detail steps, an optional recording
// offset (ms) for the seek button, and a stable key.
interface Phase {
  title: string;
  steps: ExecutionStepDetail[];
  timecodeMs?: number;
  key: string;
}

// Normalize the worker's grouped execution log into phases, tolerating the legacy flat shape
// (`{ type, text }` entries) persisted by runs created before grouping existed: each legacy entry
// becomes a title-only phase so old reports still render (just without expandable detail). Each phase
// gets a content-derived, de-duplicated key so React preserves per-phase expand state across SSE
// refetches (the run snapshot can be re-fetched while a session is still streaming) rather than the
// array index, which would jump state to a different phase when the log grows.
function toPhases(steps: ExecutionStep[]): Phase[] {
  const seen = new Map<string, number>();
  return steps
    .map((s): Omit<Phase, 'key'> | null => {
      if ('title' in s && typeof s.title === 'string') {
        return {
          title: s.title,
          steps: Array.isArray(s.steps) ? s.steps : [],
          // Only accept a finite, non-negative timecode — NaN/Infinity/negative would render an
          // empty seek chip (formatTimecode returns '') or seek the video to an invalid time.
          ...(typeof s.timecode_ms === 'number' &&
          Number.isFinite(s.timecode_ms) &&
          s.timecode_ms >= 0
            ? { timecodeMs: s.timecode_ms }
            : {}),
        };
      }
      if ('text' in s && typeof s.text === 'string')
        return { title: s.text, steps: [] }; // legacy
      return null;
    })
    .filter(
      (p): p is Omit<Phase, 'key'> => p !== null && p.title.trim().length > 0,
    )
    .map((p) => {
      // Key by title + occurrence ONLY — never step content. A phase's `steps` grow as the session
      // streams, so including them (count or text) would change the key and remount LogPhase, resetting
      // its expand state — the very thing the stable key exists to prevent. Titles can repeat, so
      // disambiguate identical titles by their occurrence order (stable as the log appends).
      const n = seen.get(p.title) ?? 0;
      seen.set(p.title, n + 1);
      return { ...p, key: `${p.title}#${n}` };
    });
}

export function ExplorationReport({
  runId,
  findings,
  executionSteps,
  artifactKeys,
  videoUrl,
  incomplete = false,
  incompleteReason,
}: {
  runId: string;
  findings: Finding[];
  executionSteps: ExecutionStep[];
  artifactKeys: string[];
  /** The run ended before a complete exploratory verdict/report could be produced. */
  incomplete?: boolean;
  incompleteReason?: string | null;
  /** Mobile only: the BrowserStack session recording URL. Web recordings come from artifactKeys
   *  instead (an uploaded .webm), so a run only ever has one or the other, never both. */
  videoUrl?: string | null;
}): JSX.Element | null {
  const shots = artifactKeys.filter((k) => IMAGE_RE.test(k));
  const videos = artifactKeys.filter((k) => VIDEO_RE.test(k));
  const phases = toPhases(executionSteps);
  const screenshotsId = useId();

  // A run has at most one recording — the web .webm (first, if several) OR the mobile URL. The
  // execution log's seek buttons target that single inline player via this ref, so a phase click
  // scrolls to the video and jumps it to the phase's moment (all within this one card — no tab
  // switch, unlike the execution-report's Cases↔Video flow). If the backend ever sent BOTH kinds,
  // artifact videos win and the external URL is suppressed, so there's exactly one player + one
  // seek target rather than two recordings with the seek hitting only the first.
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const showExternalVideo = !!videoUrl && videos.length === 0;
  const hasVideo = videos.length > 0 || showExternalVideo;
  const seekTo = (ms: number): void => {
    const v = videoRef.current;
    if (!v) return;
    v.scrollIntoView({ behavior: 'smooth', block: 'center' });
    v.pause();
    try {
      v.currentTime = ms / 1000;
    } catch {
      /* not seekable yet — metadata still loading; the user can retry */
    }
  };
  const onSeek = hasVideo ? seekTo : undefined;

  if (
    findings.length === 0 &&
    phases.length === 0 &&
    shots.length === 0 &&
    videos.length === 0 &&
    !videoUrl &&
    !incomplete
  ) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BugIcon className="h-4 w-4" aria-hidden />
          Exploration
          {findings.length > 0 && (
            <Badge variant="secondary">
              {findings.length} potential bug{findings.length === 1 ? '' : 's'}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {incomplete ? (
          <div className="flex items-start gap-2 rounded-md border border-warning-500/40 bg-warning-500/10 px-3 py-2 text-xs text-warning-600">
            <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <p>
              {incompleteReason ??
                'Exploration ended before a complete report was produced.'}{' '}
              Findings may be partial.
            </p>
          </div>
        ) : null}

        {/* Potential bugs — only rendered when an exploratory session produced findings. */}
        {findings.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] font-medium text-muted-foreground">
              Potential bugs
            </p>
            <ul className="flex flex-col gap-2">
              {findings.map((f, i) => (
                <li
                  key={i}
                  className="rounded-md border border-border bg-background px-3 py-2"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={SEVERITY_VARIANT[f.severity] ?? 'secondary'}
                      className="capitalize"
                    >
                      {f.severity}
                    </Badge>
                    <span className="text-[12px] font-medium">{f.title}</span>
                    {f.area ? (
                      <span className="text-[10px] text-muted-foreground">
                        · {f.area}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-[11px] text-muted-foreground">
                    {f.observation}
                  </p>
                  {f.screenshot ? (
                    <div className="mt-2">
                      <Shot
                        runId={runId}
                        relKey={toRelKey(runId, f.screenshot)}
                      />
                    </div>
                  ) : f.evidence_status === 'unavailable' ? (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Screenshot unavailable
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Execution log — the deterministic account of the browse, grouped into descriptive phases.
            Each phase expands to reveal the relabeled low-level actions it performed. */}
        {phases.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <FootprintsIcon className="h-3.5 w-3.5" aria-hidden />
              Execution log ({phases.length} phase
              {phases.length === 1 ? '' : 's'})
            </p>
            <ol className="flex flex-col gap-px overflow-hidden rounded-md border border-border">
              {phases.map((p, i) => (
                <LogPhase
                  key={p.key}
                  index={i}
                  phase={p}
                  onSeek={onSeek}
                  screenshotsId={shots.length > 0 ? screenshotsId : undefined}
                />
              ))}
            </ol>
          </div>
        )}

        {/* Recording — web: the Playwright capture, served via the authed artifact endpoint. mobile:
            the BrowserStack session recording, a direct external URL (already carries its own
            access token) — a plain <video>, NOT AuthVideo, which fetches through our own
            bearer-authed endpoint and would 401/CORS-fail against a third-party host. */}
        {videos.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <VideoIcon className="h-3.5 w-3.5" aria-hidden />
              Recording{videos.length === 1 ? '' : `s (${videos.length})`}
            </p>
            <div className="flex flex-col gap-2">
              {videos.map((k, i) => (
                <AuthVideo
                  key={k}
                  // The seek buttons target the first recording (a run has one browse video).
                  ref={i === 0 ? videoRef : undefined}
                  src={artifactUrl(runId, toRelKey(runId, k))}
                  className="max-h-[70vh] w-full rounded-md"
                />
              ))}
            </div>
          </div>
        )}

        {showExternalVideo && (
          <div className="flex flex-col gap-1.5">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <VideoIcon className="h-3.5 w-3.5" aria-hidden />
              Recording
            </p>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- session recordings have no captions */}
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              preload="metadata"
              className="max-h-[70vh] w-full rounded-md bg-black"
            />
          </div>
        )}

        {shots.length > 0 && (
          <div id={screenshotsId} className="flex flex-col gap-1.5 scroll-mt-4">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <CameraIcon className="h-3.5 w-3.5" aria-hidden />
              Screenshots ({shots.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {shots.map((k) => (
                <Shot key={k} runId={runId} relKey={toRelKey(runId, k)} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// One execution-log phase: a numbered, descriptive row that expands to reveal the relabeled low-level
// actions it performed. Title-only phases (no detail — e.g. the agent's summary or the time-limit
// note) render as a plain, non-expandable row.
function LogPhase({
  index,
  phase,
  onSeek,
  screenshotsId,
}: {
  index: number;
  phase: Phase;
  /** Present only when the run has a recording; seeks it to `ms`. */
  onSeek?: (ms: number) => void;
  screenshotsId?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const expandable = phase.steps.length > 0;
  const canSeek = onSeek && typeof phase.timecodeMs === 'number';
  return (
    <li className="bg-background">
      {/* The toggle and the seek control are SIBLINGS (not a nested button — invalid HTML): the
          expand affordance spans the row, the seek chip sits at its right edge. */}
      <div
        className={cn('flex items-start', expandable && 'hover:bg-muted/40')}
      >
        <button
          type="button"
          disabled={!expandable}
          aria-expanded={expandable ? open : undefined}
          onClick={() => expandable && setOpen((o) => !o)}
          className={cn(
            'flex min-w-0 flex-1 items-start gap-2 px-3 py-1.5 text-left text-[11px]',
            expandable && 'cursor-pointer',
          )}
        >
          <span className="mt-0.5 w-5 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">
            {index + 1}
          </span>
          {expandable ? (
            <ChevronRightIcon
              className={cn(
                'mt-0.5 h-3 w-3 shrink-0 text-muted-foreground transition-transform',
                open && 'rotate-90',
              )}
              aria-hidden
            />
          ) : (
            <span className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          )}
          <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-foreground">
            {phase.title}
          </span>
          {expandable && (
            <Badge
              variant="secondary"
              className="shrink-0 px-1.5 py-0 text-[10px] tabular-nums"
            >
              {phase.steps.length}
            </Badge>
          )}
        </button>
        {canSeek ? (
          <button
            type="button"
            onClick={() => onSeek!(phase.timecodeMs!)}
            className="mr-2 mt-1 inline-flex shrink-0 items-center gap-1 rounded-full border border-border px-1.5 py-0.5 font-mono text-[0.65rem] text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
            title="Jump to this moment in the recording"
            aria-label={`Jump to ${formatTimecode(phase.timecodeMs)} in the recording`}
          >
            <PlayCircleIcon className="size-3" aria-hidden />
            {formatTimecode(phase.timecodeMs)}
          </button>
        ) : null}
      </div>
      {expandable && open && (
        <ul className="flex flex-col gap-px border-t border-border bg-muted/20 px-3 py-1.5 pl-10">
          {phase.steps.map((s, j) => (
            <li key={j} className="flex items-start gap-2 py-0.5 text-[11px]">
              <MousePointerClickIcon
                className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <span className="min-w-0 flex-1 break-words font-mono text-foreground">
                {s.text}
                {s.tool?.split('__').at(-1) === 'browser_take_screenshot' &&
                  (screenshotsId ? (
                    <a
                      href={`#${screenshotsId}`}
                      className="ml-2 text-primary underline"
                    >
                      View screenshots
                    </a>
                  ) : (
                    <span className="ml-2 text-muted-foreground">
                      No saved screenshot available
                    </span>
                  ))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Shot({
  runId,
  relKey,
}: {
  runId: string;
  relKey: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  // Use the screenshot's filename so each image has a distinct alt / aria label for screen readers.
  const name = relKey.split('/').pop() || relKey;
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      aria-expanded={open}
      className="overflow-hidden rounded border border-border transition-shadow hover:shadow-elev-1"
      aria-label={`${open ? 'Collapse' : 'Expand'} exploration screenshot ${name}`}
    >
      <AuthImage
        src={artifactUrl(runId, relKey)}
        alt={`Exploration screenshot ${name}`}
        className={cn(
          'object-cover',
          open ? 'max-h-[70vh] w-auto' : 'h-20 w-28',
        )}
      />
    </button>
  );
}
